import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { ColorSurvey } from './ColorSurvey.js';
import { PsychometricsPane } from './PsychometricsPane.js';
import { COLOR_META, type ColorKey, type PersonalitySurveyResult } from '../lib/colorSurvey.js';

interface Relationship { name: string; relation: string; note: string }
interface PersonaProfile {
  bio: string;
  digitalTwinName: string;
  values: string[];
  traits: string[];
  speakingStyle: string;
  recurringPhrases: string[];
  relationships: Relationship[];
  beliefs: string[];
  personalitySurvey?: PersonalitySurveyResult;
  updatedAt: string;
  generatedFrom?: { memoryCount: number; at: string };
}

const EMPTY: PersonaProfile = {
  bio: '', digitalTwinName: '', values: [], traits: [], speakingStyle: '', recurringPhrases: [],
  relationships: [], beliefs: [], updatedAt: '',
};

function CsvField({ label, value, onChange, placeholder }: {
  label: string; value: string[]; onChange: (v: string[]) => void; placeholder?: string;
}) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-5 items-start py-3 border-b border-ink/[0.06]">
      <div className="text-[11px] text-ink/45 pt-2">{label}</div>
      <input
        value={value.join(', ')}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
        className="w-full px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[15px] text-ink focus:border-ink/40 focus:outline-none"
      />
    </div>
  );
}

function TextField({ label, value, onChange, rows = 3, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; rows?: number; placeholder?: string;
}) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-5 items-start py-3 border-b border-ink/[0.06]">
      <div className="text-[11px] text-ink/45 pt-2">{label}</div>
      <textarea
        value={value} rows={rows} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[15px] text-ink focus:border-ink/40 focus:outline-none resize-none"
      />
    </div>
  );
}

export function PersonaPane() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<{ persona: PersonaProfile }>({
    queryKey: ['persona'],
    queryFn: () => api('/api/persona'),
  });

  const [draft, setDraft] = useState<PersonaProfile>(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [genMsg, setGenMsg] = useState<string | null>(null);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const inited = useRef(false);

  useEffect(() => {
    if (data?.persona && !inited.current) { setDraft({ ...EMPTY, ...data.persona }); inited.current = true; }
  }, [data]);

  function patch(p: Partial<PersonaProfile>) { setDraft((d) => ({ ...d, ...p })); setDirty(true); }

  async function save() {
    setSaving(true);
    try {
      await api('/api/persona', { method: 'PUT', body: JSON.stringify({ persona: draft }) });
      qc.invalidateQueries({ queryKey: ['persona'] });
      setDirty(false);
    } finally { setSaving(false); }
  }

  async function generate() {
    setGenMsg('Analyzing the vault…');
    try {
      const r = await api<{ ok: boolean; persona: PersonaProfile }>('/api/persona/generate', { method: 'POST' });
      setDraft({ ...EMPTY, ...r.persona });
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['persona'] });
      setGenMsg('Generated from your memories.');
    } catch {
      setGenMsg('No API key in dev — ask Claude Desktop: “analyse mes souvenirs et appelle save_persona”.');
    }
  }

  if (isLoading) return <div className="text-ink/40 text-[12px] py-8">Loading…</div>;
  if (error) return <div className="text-red-300 text-[12px] py-8">Couldn’t load persona.</div>;

  return (
    <>
      <div className="mb-4">
        <h3 className="font-serif text-2xl text-ink m-0">Persona</h3>
        <p className="font-serif italic text-sm text-ink/55 mt-1 max-w-prose">
          The distilled “who you are” — the brain the clone speaks from. Build it from your memories,
          then refine by hand. {draft.generatedFrom && (
            <span className="text-ink/40">Last generated from {draft.generatedFrom.memoryCount} memories.</span>
          )}
        </p>
      </div>

      <div className="flex flex-col">
        <div className="grid grid-cols-[160px_1fr] gap-5 items-start py-3 border-b border-ink/[0.06]">
          <div className="text-[11px] text-ink/45 pt-2">Digital twin name</div>
          <div>
            <input
              value={draft.digitalTwinName}
              placeholder="e.g. Nitram"
              onChange={(e) => patch({ digitalTwinName: e.target.value })}
              className="w-48 px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[15px] text-ink focus:border-ink/40 focus:outline-none" />
            <p className="text-[11px] text-ink/35 mt-1.5">
              The name the twin gives itself when asked. It always identifies as a digital twin of its owner — never as the flesh-and-blood person.
            </p>
          </div>
        </div>

        {/* Personality / Color survey */}
        <div className="grid grid-cols-[160px_1fr] gap-5 items-start py-3 border-b border-ink/[0.06]">
          <div className="text-[11px] text-ink/45 pt-2">Color survey</div>
          <div className="space-y-2">
            {draft.personalitySurvey ? (
              <SurveyResultBlock result={draft.personalitySurvey} onRetake={() => setSurveyOpen(true)} />
            ) : (
              <div className="flex items-center gap-3">
                <button onClick={() => setSurveyOpen(true)}
                  className="px-4 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle">
                  Take the color survey
                </button>
                <span className="font-serif italic text-[12px] text-ink/45">~5 min · True-Colors temperament profile</span>
              </div>
            )}
          </div>
        </div>

        {/* Psychometric value & moral profile (epic 71) */}
        <PsychometricsPane />

        <TextField label="Bio (first person)" value={draft.bio} rows={4}
          placeholder="Je suis [ton nom], né(e) à…" onChange={(v) => patch({ bio: v })} />
        <CsvField label="Values" value={draft.values} placeholder="famille, honnêteté, curiosité" onChange={(v) => patch({ values: v })} />
        <CsvField label="Traits" value={draft.traits} placeholder="posé, taquin, généreux" onChange={(v) => patch({ traits: v })} />
        <CsvField label="Beliefs" value={draft.beliefs} placeholder="le travail honnête finit par payer" onChange={(v) => patch({ beliefs: v })} />
        <TextField label="Speaking style" value={draft.speakingStyle} rows={2}
          placeholder="Parle franc, glisse des expressions québécoises, humour pince-sans-rire." onChange={(v) => patch({ speakingStyle: v })} />
        <CsvField label="Recurring phrases" value={draft.recurringPhrases} placeholder="« voyons donc », « c’est ben correct »" onChange={(v) => patch({ recurringPhrases: v })} />

        {/* Relationships */}
        <div className="grid grid-cols-[160px_1fr] gap-5 items-start py-3 border-b border-ink/[0.06]">
          <div className="text-[11px] text-ink/45 pt-2">People</div>
          <div className="flex flex-col gap-2">
            {draft.relationships.map((r, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input value={r.name} placeholder="name"
                  onChange={(e) => { const rs = [...draft.relationships]; rs[i] = { ...r, name: e.target.value }; patch({ relationships: rs }); }}
                  className="w-28 px-2 py-1.5 rounded-md border border-ink/15 bg-white/[0.02] font-serif text-[13px] text-ink focus:border-ink/40 focus:outline-none" />
                <input value={r.relation} placeholder="relation"
                  onChange={(e) => { const rs = [...draft.relationships]; rs[i] = { ...r, relation: e.target.value }; patch({ relationships: rs }); }}
                  className="w-32 px-2 py-1.5 rounded-md border border-ink/15 bg-white/[0.02] font-serif text-[13px] text-ink focus:border-ink/40 focus:outline-none" />
                <input value={r.note} placeholder="note"
                  onChange={(e) => { const rs = [...draft.relationships]; rs[i] = { ...r, note: e.target.value }; patch({ relationships: rs }); }}
                  className="flex-1 px-2 py-1.5 rounded-md border border-ink/15 bg-white/[0.02] font-serif text-[13px] text-ink focus:border-ink/40 focus:outline-none" />
                <button onClick={() => { const rs = draft.relationships.filter((_, j) => j !== i); patch({ relationships: rs }); }}
                  className="text-red-300/70 hover:text-red-300 font-mono text-[10px] px-1">✕</button>
              </div>
            ))}
            <button onClick={() => patch({ relationships: [...draft.relationships, { name: '', relation: '', note: '' }] })}
              className="self-start px-3 py-1 rounded-full border border-ink/20 text-[11px] text-ink/50 hover:text-ink">+ person</button>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-ink/10 flex items-center gap-3 flex-wrap">
        <button onClick={save} disabled={!dirty || saving}
          className="px-5 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-30">
          {saving ? 'Saving…' : dirty ? 'Save to database' : 'Saved'}
        </button>
        <button onClick={generate}
          className="px-4 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink">
          Generate from vault
        </button>
        {dirty && <span className="text-[11px] text-amber/70">unsaved</span>}
        {genMsg && <span className="font-serif italic text-[12px] text-ink/55">{genMsg}</span>}
      </div>

      <ColorSurvey
        open={surveyOpen}
        onClose={() => setSurveyOpen(false)}
        onSaved={(result) => {
          setDraft((d) => ({ ...d, personalitySurvey: result }));
          qc.invalidateQueries({ queryKey: ['persona'] });
          setSurveyOpen(false);
        }}
      />
    </>
  );
}

function SurveyResultBlock({ result, onRetake }: { result: PersonalitySurveyResult; onRetake: () => void }) {
  const primary = COLOR_META[result.primary];
  const secondary = COLOR_META[result.secondary];
  const order: ColorKey[] = ['blue', 'gold', 'green', 'orange'];
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="px-2.5 py-0.5 rounded-full text-[11.5px]" style={{ background: primary.hex, color: '#0a0a0a' }}>{primary.label}</span>
        <span className="font-mono text-[10px] text-ink/45">primary · {result.percents[result.primary]}%</span>
        <span className="text-ink/25">·</span>
        <span className="px-2 py-0.5 rounded-full text-[11px] border" style={{ color: secondary.hex, borderColor: secondary.hex + '60' }}>{secondary.label}</span>
        <span className="font-mono text-[10px] text-ink/45">{result.percents[result.secondary]}%</span>
      </div>
      <div className="flex flex-col gap-1">
        {order.map((k) => (
          <div key={k} className="flex items-center gap-2">
            <span className="w-12 text-[11px] text-ink/45">{COLOR_META[k].label}</span>
            <div className="flex-1 h-1.5 rounded-full bg-ink/10 overflow-hidden">
              <div className="h-full" style={{ width: `${result.percents[k]}%`, background: COLOR_META[k].hex }} />
            </div>
            <span className="w-9 text-right font-mono text-[10px] text-ink/55 tabular-nums">{result.percents[k]}%</span>
          </div>
        ))}
      </div>
      <button onClick={onRetake} className="text-[11px] text-ink/50 hover:text-ink">retake →</button>
    </div>
  );
}
